import React, { createContext, useContext, ReactNode } from "react";
import { useOrganization } from "~hooks";

interface StripeConfigContextType {
  integrationId?: string;
  publishableKey?: string;
  connectedAccountId?: string;
  expressCheckoutEnabled?: boolean;
}

const StripeConfigContext = createContext<StripeConfigContextType | undefined>(
  undefined
);

interface StripeProviderProps extends StripeConfigContextType {
  children: ReactNode;
  config?: StripeConfigContextType;
}

export const StripeConfigProvider: React.FC<StripeProviderProps> = ({
  config = {},
  children,
}) => {
  const { hasFeature } = useOrganization();

  // Express checkout is only enabled for customers if the organization has a Stripe connect account
  // Legacy stripe accounts are used for internal demoing purposes only via "legacyStripeExpressCheckout"
  const allowStripeAccount =
    (!!config?.connectedAccountId && config.expressCheckoutEnabled) ||
    hasFeature("StripeExpressCheckoutLegacy");

  const expressCheckoutEnabled =
    hasFeature("StripeExpressCheckout") &&
    allowStripeAccount &&
    !!config?.publishableKey;

  return (
    <StripeConfigContext.Provider
      value={{
        ...config,
        expressCheckoutEnabled,
      }}
    >
      {children}
    </StripeConfigContext.Provider>
  );
};

export const useStripeConfigContext = () => {
  const context = useContext(StripeConfigContext);
  if (!context) {
    console.error(
      "useStripeConfigContext must be used within a StripeProvider"
    );
  }
  return context;
};
